import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import {
    FrameworkColumn,
    IDocument,
    ITableCellValueRenderer,
    SafeAny,
} from '@pf/shared-common';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface PfThumbnailCellRendererProps {
    resolveThumbnail$: (row: SafeAny) => Observable<IDocument | null>;
    resolvePreviewUrl$?: (doc: IDocument, row: SafeAny) => Observable<string>;
}

@Component({
    selector: 'pf-thumbnail-cell',
    template: `
        @let doc = doc$ | async;
        @let src = displaySrc$ | async;
        @if (src) {
            <div
                class="pf-thumbnail-cell__wrap pf-thumbnail-cell__wrap--fixed"
                nz-popover
                [nzPopoverContent]="previewTpl"
                nzPopoverTrigger="hover"
                nzPopoverPlacement="right">
                <img
                    class="pf-thumbnail-cell__img"
                    [src]="src"
                    [alt]="doc?.fileName ?? placeholderLabel"
                    loading="lazy" />
            </div>
            <ng-template #previewTpl>
                <div class="pf-thumbnail-cell__preview-wrap">
                    <img
                        class="pf-thumbnail-cell__preview"
                        [src]="src"
                        [alt]="doc?.fileName ?? placeholderLabel"
                        loading="lazy" />
                </div>
            </ng-template>
        } @else {
            <span
                class="pf-thumbnail-cell__placeholder"
                role="img"
                [attr.aria-label]="placeholderLabel">
                <pf-icon iconType="item" size="md"></pf-icon>
            </span>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .pf-thumbnail-cell__wrap {
                display: inline-flex;
                cursor: zoom-in;
                line-height: 0;
                border-radius: 4px;
            }
            .pf-thumbnail-cell__wrap--fixed {
                width: 40px;
                height: 40px;
                overflow: hidden;
            }
            .pf-thumbnail-cell__img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 4px;
                vertical-align: middle;
            }
            .pf-thumbnail-cell__preview {
                display: block;
                max-width: min(280px, 70vw);
                max-height: min(280px, 50vh);
                width: auto;
                height: auto;
                object-fit: contain;
                border-radius: 4px;
            }
            .pf-thumbnail-cell__preview-wrap {
                line-height: 0;
            }
            .pf-thumbnail-cell__placeholder {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                background: var(--pf-color-neutral-100, #f0f0f0);
                border-radius: 4px;
                color: var(--pf-color-neutral-400, #bfbfbf);
            }
        `,
    ],
    standalone: false,
})
export class PfThumbnailCellComponent
    implements
        ITableCellValueRenderer,
        PfThumbnailCellRendererProps,
        OnChanges,
        OnDestroy
{
    @Input() value!: SafeAny;
    @Input() column!: FrameworkColumn;
    @Input() row!: SafeAny;
    @Input() resolveThumbnail$!: (row: SafeAny) => Observable<IDocument | null>;
    @Input() resolvePreviewUrl$?: (
        doc: IDocument,
        row: SafeAny
    ) => Observable<string>;
    @Input() placeholderLabel = 'No image';

    doc$: Observable<IDocument | null> = of(null);
    displaySrc$: Observable<string | null> = of(null);

    private previewObjectUrl: string | null = null;

    ngOnChanges(_changes: SimpleChanges): void {
        if (!this.resolveThumbnail$) {
            this.revokePreviewObjectUrl();
            this.doc$ = of(null);
            this.displaySrc$ = of(null);
            return;
        }
        this.doc$ = this.resolveThumbnail$(this.row);
        this.displaySrc$ = this.buildDisplaySrc$(this.doc$);
    }

    ngOnDestroy(): void {
        this.revokePreviewObjectUrl();
    }

    private revokePreviewObjectUrl(): void {
        if (this.previewObjectUrl) {
            URL.revokeObjectURL(this.previewObjectUrl);
            this.previewObjectUrl = null;
        }
    }

    private buildDisplaySrc$(
        doc$: Observable<IDocument | null>
    ): Observable<string | null> {
        return doc$.pipe(
            switchMap(doc => {
                this.revokePreviewObjectUrl();
                if (!doc) {
                    return of(null);
                }
                const fileId = doc.fileId;
                if (fileId && this.resolvePreviewUrl$) {
                    return this.resolvePreviewUrl$(doc, this.row).pipe(
                        map(url => {
                            this.previewObjectUrl = url;
                            return url;
                        }),
                        catchError(() => of(doc.url ?? null))
                    );
                }
                return of(doc.url ?? null);
            })
        );
    }
}
